import { Base, Component, Emit, Prop } from '@/vue-property-decorator'
import { CURRENT_LANGUAGE_CONFIG, rectIconMap } from '@/config/lang.config'
import { GlobalConst } from '@/context'
import { Icon, Menu, Row } from '@/plugins/ant-design-vue/tsx-support'
import { Language } from '@/i18n'
import { cloneDeep, invert } from 'lodash'
import { createPngSprite, createSvgSprite } from '@/utils/business-utils/assets'
import { useMainStore } from '@/store/index'
import style from '../style.module.scss'

import { CanClickWhenForceBind } from '@/views/center/security/store'
import Skin from '@/controller/Skin'

interface Props {
  showIcon?: boolean
}

interface State {
  langKey: string
}

/**
 * 语言选项列表 因为H5是弹窗形式PC是下拉形式，所以抽成独立组件复用
 * */
@Component<SwitchLangOptions>({
  name: 'SwitchLangOptions'
})
export default class SwitchLangOptions extends Base<unknown, Props> {
  state: State = {
    langKey: ''
  }
  @Prop({ default: false })
  readonly showIcon?: Props['showIcon']
  loading = false
  @Emit('selected')
  private selected() {
    return
  }

  @Emit('loadState')
  private loadState(load = false) {
    this.loading = load
    return load
  }
  private get selectedKey() {
    const appStore = useMainStore()
    return appStore.language
  }
  private get langList() {
    const appStore = useMainStore()
    return (
      cloneDeep(appStore?.siteInfos?.languageInfos || [])
        .sort((it) => (it.defaultLanguageTag ? -1 : 1))
        .filter((it) => CURRENT_LANGUAGE_CONFIG.includes(it.languageCode)) || []
    )
  }
  /** 监听语言切换 */
  private handlerSelect = async (info: Record<'key', Language>) => {
    this.loadState(true)
    this.setState({
      langKey: info.key
    })
    setTimeout(async () => {
      const appStore = useMainStore()
      await appStore.setLanguage(info.key).finally(() => {
        this.loadState(false)
      })
      const docEle = document.documentElement
      docEle.setAttribute('lang', info.key)
      Object.keys(localStorage)
        .filter((k) => /^web\.lobby\./.test(k))
        .forEach((k) => localStorage.removeItem(k))
      this.selected()
    }, 200)
  }

  private renderCountryIcon(lang: Language) {
    return (
      <Icon.Tsx
        class={['switch-lang__icon']}
        style={{ display: 'flex', alignItems: 'center' }}
        component={{
          functional: true,
          render: () => {
            const icon = rectIconMap[lang]()
            const scale = 20 / icon?.width
            return <icon-sprite scale={scale} sprite={icon}></icon-sprite>
          }
        }}
      />
    )
  }

  private renderItem({
    name,
    lang,
    selected
  }: {
    lang: Language
    name: string
    selected: boolean
  }) {
    const { isWeb } = useMainStore()
    const { isGalaxyGold, isCrownGold } = Skin.currentTheme()
    return (
      <Row.Tsx
        title={name}
        class={[style.item, CanClickWhenForceBind]}
        type={'flex'}
        align={'middle'}
      >
        {(isGalaxyGold || isCrownGold) && isWeb && this.renderCountryIcon(lang)}
        <span
          class={[GlobalConst.CssClassName.Lobby.Ellipsis, 'switch-lang__text']}
        >
          {name}
        </span>
        {!isWeb && selected ? (
          <icon-sprite
            class={style['switch-lang-animate']}
            scale={30 / 41}
            sprite={createPngSprite('sprite_main_comm_icon_loading')}
          />
        ) : (
          this.showIcon && (
            <div
              class={{
                [style.cicle]: true,
                ['cicle']: true
              }}
            >
              <icon-sprite sprite={createSvgSprite('comm_icon_gou')} />
            </div>
          )
        )}
      </Row.Tsx>
    )
  }

  render() {
    return (
      <Menu.Tsx
        class={[style.menu, 'menu']}
        selectable
        selected-keys={[this.selectedKey]}
        onSelect={this.handlerSelect}
      >
        {this.langList.map((item) => {
          const { languageCode, languageTranslateName } = item
          const key = invert(GlobalConst.ServiceLanguageMap)[
            languageCode
          ] as Language
          return (
            <Menu.Item.Tsx key={key}>
              {this.renderItem({
                lang: key,
                name: languageTranslateName,
                selected: this.loading && key === this.state.langKey
              })}
            </Menu.Item.Tsx>
          )
        })}
      </Menu.Tsx>
    )
  }
}
